import React, { useState, useEffect } from 'react';
import {
  Badge,
  Button,
  Calendar,
  Card,
  DatePicker,
  Empty,
  Grid,
  Image,
  List,
  message,
  Modal,
  Space,
  Tag,
  Typography,
  Alert,
  Input,
  Select,
  Radio
} from 'antd';
import { ShoppingCartOutlined, ClockCircleOutlined, InfoCircleOutlined, MinusOutlined, PlusOutlined, DeleteOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { API_BASE_URL, linkRequestsAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { categoryLabel } from '../utils/labels';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

interface MenuItem {
  id: number;
  name: string;
  description?: string;
  category: string;
  weight: number;
  calories?: number | null;
  price: number;
  originalPrice?: number;
  discountPercent?: number;
  isAvailable: boolean;
  imageUrl?: string;
  thumbnailUrl?: string;
  imageWidth?: number;
  imageHeight?: number;
  supplierId: number;
  isVegan?: boolean;
  isLenten?: boolean;
  isHalal?: boolean;
}

type ComboPricingMode = 'fixed' | 'sum_items';

interface ComboGroupItem {
  id: number;
  menuItemId: number;
  sortOrder: number;
  menuItem: MenuItem;
}

interface ComboGroup {
  id: number;
  title: string;
  requiredCount: number;
  sortOrder: number;
  items: ComboGroupItem[];
}

interface Combo {
  id: number;
  title: string;
  description?: string | null;
  allowCustomization: boolean;
  pricingMode: ComboPricingMode;
  fixedPrice?: number | null;
  groups: ComboGroup[];
}

interface DayMenu {
  date: string;
  items: MenuItem[];
  combosOnly?: boolean;
  combos?: Combo[];
  orderDeadline?: string; // Время до которого можно заказать (например "09:00")
  minOrders?: number; // Минимальное количество заказов
  currentOrders?: number; // Текущее количество заказов
  discountApplied?: boolean;
  discountPercent?: number;
  minOrderAmount?: number;
  emergencyAllowed?: boolean;
  emergencyActive?: boolean;
  emergencyExpiresAt?: string | null;
  /** До этого времени (ЧЧ:мм) в день доставки сотрудник может отказаться (в т.ч. после отправки заказа) */
  employeeCancelDeadlineTime?: string | null;
  supplier?: { id: number; name: string } | null;
}

interface OrderItem {
  id: number;
  menuItemId: number;
  menuItem: MenuItem;
  quantity: number;
  date: string;
  poolStatus?: string;
  /** false только после подтверждения заказчиком; status=sent сам по себе не блокирует */
  poolEditable?: boolean;
  employeeNote?: string | null;
}

interface ComboOrderItem {
  id: number;
  comboId: number;
  combo: Combo;
  quantity: number;
  unitPrice: number;
  priceTotal: number;
  selectionsSnapshot: Array<{
    groupId: number;
    title: string;
    requiredCount: number;
    items: Array<{ menuItemId: number; name: string; price: number }>;
  }>;
  date: string;
  poolStatus?: string;
  poolEditable?: boolean;
}

const Menu: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Dayjs>(() => dayjs().add(1, 'day').startOf('day'));
  const [dayMenu, setDayMenu] = useState<DayMenu | null>(null);
  const [myOrders, setMyOrders] = useState<OrderItem[]>([]);
  const [myComboOrders, setMyComboOrders] = useState<ComboOrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [emergencyStatus, setEmergencyStatus] = useState<{
    allowed: boolean;
    active: boolean;
    expiresAt: string | null;
  }>({
    allowed: false,
    active: false,
    expiresAt: null
  });
  const [limitsInfo, setLimitsInfo] = useState<{
    hasLimits: boolean;
    dailyLimit: number | null;
    monthlyLimit: number | null;
    dailySpent: number;
    monthlySpent: number;
    dailyRemaining: number | null;
    monthlyRemaining: number | null;
    canOrder?: boolean;
    maxAdditional?: number | null;
    blockReason?: string | null;
  } | null>(null);
  const [submissionStatus, setSubmissionStatus] = useState<Record<string, { submitted: boolean; allowEdit: boolean }>>({});
  const [employeeCancelDeadlineTime, setEmployeeCancelDeadlineTime] = useState<string | null>(null);
  const [orderingMenu, setOrderingMenu] = useState<{
    orderingSupplierId: number | null;
    suppliers: Array<{ id: number; name: string }>;
    canSwitch: boolean;
  } | null>(null);
  const [switchingMenu, setSwitchingMenu] = useState(false);
  const { user } = useAuthStore();
  const isCustomer = user?.role === 'customer';
  const isCustomerOrManager = user?.role === 'customer' || user?.role === 'customer_manager';
  const canOrderFood = user?.role === 'employee' || user?.role === 'customer_manager';
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const getLimitBlockForPrice = React.useCallback(
    (price: number): string | null => {
      if (!limitsInfo?.hasLimits) return null;
      if (limitsInfo.canOrder === false) {
        return (
          limitsInfo.blockReason ||
          'Лимит исчерпан — удалите лишнее из корзины или обратитесь к заказчику.'
        );
      }
      if (limitsInfo.maxAdditional != null && price > limitsInfo.maxAdditional + 0.001) {
        return `Недостаточно лимита. Можно добавить ещё на ${limitsInfo.maxAdditional} ₽`;
      }
      return null;
    },
    [limitsInfo]
  );

  const computeComboDisplayPrice = React.useCallback((combo: Combo): number => {
    if (combo.pricingMode === 'fixed') {
      return typeof combo.fixedPrice === 'number' ? combo.fixedPrice : 0;
    }
    return combo.groups.reduce(
      (sum, g) => sum + (g.items || []).reduce((s2, it) => s2 + (it.menuItem?.price || 0), 0),
      0
    );
  }, []);

  const buildSelectionsForCombo = React.useCallback((combo: Combo, chosen?: Record<number, number[]>) => {
    const selections: Record<string, number[]> = {};
    combo.groups.forEach((g) => {
      const ids =
        chosen?.[g.id] ??
        (combo.allowCustomization ? [] : (g.items || []).map((it) => it.menuItemId));
      selections[String(g.id)] = ids;
    });
    return selections;
  }, []);

  const [comboModalOpen, setComboModalOpen] = useState(false);
  const [comboModalCombo, setComboModalCombo] = useState<Combo | null>(null);
  const [comboSelections, setComboSelections] = useState<Record<number, number[]>>({});

  const openComboOrderModal = React.useCallback((combo: Combo) => {
    setComboModalCombo(combo);
    const initial: Record<number, number[]> = {};
    combo.groups.forEach((g) => {
      initial[g.id] = combo.allowCustomization ? [] : (g.items || []).map((it) => it.menuItemId);
    });
    setComboSelections(initial);
    setComboModalOpen(true);
  }, []);

  const comboSelectionErrors = React.useMemo(() => {
    const combo = comboModalCombo;
    if (!combo) return [];
    const errors: string[] = [];
    (combo.groups || []).forEach((g) => {
      const chosen = comboSelections[g.id] || [];
      const required = g.requiredCount || 1;
      if (chosen.length !== required) {
        errors.push(`«${g.title}»: выберите ${required}`);
      }
    });
    return errors;
  }, [comboModalCombo, comboSelections]);

  const isOrderingForToday = selectedDate.isSame(dayjs(), 'day');

  const confirmSameDayOrder = React.useCallback((): Promise<boolean> => {
    if (!isOrderingForToday || emergencyStatus.active) {
      return Promise.resolve(true);
    }
    const tomorrow = dayjs().add(1, 'day');
    return new Promise((resolve) => {
      Modal.confirm({
        title: 'Заказ на сегодня',
        icon: <InfoCircleOutlined />,
            content: (
          <div>
            <p>
              Сейчас открыто меню <strong>на сегодня</strong> ({selectedDate.format('DD.MM.YYYY')}) —
              блюдо попадёт в заказ на этот же день.
            </p>
            <p style={{ marginBottom: 0 }}>
              Обычно нужно меню <strong>на завтра</strong> ({tomorrow.format('DD.MM.YYYY')}).
              Заказ из сегодняшнего меню часто не успевает до поставщика (дедлайн / автоотмена).
            </p>
          </div>
        ),
        okText: 'Оставить сегодня',
        cancelText: 'Открыть завтра',
        onOk: () => resolve(true),
        onCancel: () => {
          setSelectedDate(tomorrow.startOf('day'));
          resolve(false);
        },
      });
    });
  }, [emergencyStatus.active, isOrderingForToday, selectedDate]);

  const submitComboOrder = React.useCallback(
    async (combo: Combo, selectionsOverride?: Record<number, number[]>) => {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      if (!canOrderForDate(selectedDate)) {
        if (selectedDate.day() === 1) {
          message.warning(
            'Заказ на понедельник откроется в воскресенье с 18:01 — после обновления лимитов по часам до 18:00'
          );
        } else {
          message.warning('Заказ на эту дату невозможен');
        }
        return;
      }
      if (!(await confirmSameDayOrder())) return;

      const comboPrice = computeComboDisplayPrice(combo);
      const limitBlock = getLimitBlockForPrice(comboPrice);
      if (limitBlock) {
        message.error(limitBlock);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const selections = buildSelectionsForCombo(combo, selectionsOverride);
        const response = await fetch(`${API_BASE_URL}/api/daily-menu/add-combo`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ comboId: combo.id, date: dateStr, selections })
        });
        if (response.ok) {
          message.success(`Комбо «${combo.title}» добавлено в заказ на ${selectedDate.format('DD.MM.YYYY')}`);
          setComboModalOpen(false);
          await loadMyOrders();
          loadLimits(selectedDate);
        } else {
          const err = await response.json().catch(() => ({}));
          message.error(err?.error || 'Не удалось добавить комбо');
        }
      } catch {
        message.error('Ошибка соединения с сервером');
      }
    },
    [
      API_BASE_URL,
      buildSelectionsForCombo,
      canOrderForDate,
      computeComboDisplayPrice,
      confirmSameDayOrder,
      getLimitBlockForPrice,
      loadMyOrders,
      selectedDate,
    ]
  );

  const removeComboOrderItem = React.useCallback(
    async (comboPoolItemId: number, decrement?: boolean) => {
      try {
        const token = localStorage.getItem('token');
        const q = decrement ? '?decrement=true' : '';
        const resp = await fetch(`${API_BASE_URL}/api/daily-menu/remove-combo/${comboPoolItemId}${q}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.ok) {
          await loadMyOrders();
        } else {
          const err = await resp.json().catch(() => ({}));
          message.error(err?.error || 'Не удалось удалить комбо');
        }
      } catch {
        message.error('Ошибка соединения');
      }
    },
    [API_BASE_URL, loadMyOrders]
  );

  const isEmployeeCancelWindowOpen = React.useCallback(
    (deliveryDateStr: string) => {
      if (!employeeCancelDeadlineTime) return false;
      const m = employeeCancelDeadlineTime.match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return false;
      const h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      if (Number.isNaN(h) || Number.isNaN(min)) return false;
      const deadline = dayjs(deliveryDateStr).startOf('day').hour(h).minute(min);
      return dayjs().isSameOrBefore(deadline);
    },
    [employeeCancelDeadlineTime]
  );

  /** Пул заблокирован только после подтверждения заказчиком; status=sent (авто-крон) ещё можно править. */
  const isPoolLocked = React.useCallback(
    (orders: Array<{ poolEditable?: boolean; poolStatus?: string }>) =>
      orders.some((o) => o.poolEditable === false),
    []
  );

  const resolveImageUrl = React.useCallback((path?: string | null) => {
    if (!path) return undefined;
    return path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  }, []);

  useEffect(() => {
    loadMenuForDate(selectedDate);
    loadMyOrders();
    loadLimits(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    if (!isCustomerOrManager) return;
    linkRequestsAPI
      .getOrderingMenu()
      .then((data) => setOrderingMenu(data))
      .catch(() => setOrderingMenu(null));
  }, [isCustomerOrManager]);

  const handleSwitchOrderingMenu = async (supplierId: number) => {
    if (!orderingMenu || supplierId === orderingMenu.orderingSupplierId) return;
    const next = orderingMenu.suppliers.find((s) => s.id === supplierId);
    let pending = { willAnnul: false, employeeCount: 0, itemCount: 0 };
    try {
      pending = await linkRequestsAPI.getOrderingMenuPendingCarts(supplierId);
    } catch {
      message.error('Не удалось проверить корзины сотрудников');
      return;
    }

    Modal.confirm({
      title: 'Сменить меню для заказов?',
      content: (
        <div>
          <p>
            Сотрудники будут видеть и заказывать только меню поставщика{' '}
            <strong>{next?.name || `#${supplierId}`}</strong>.
          </p>
          {pending.willAnnul ? (
            <p style={{ color: '#cf1322', marginBottom: 0 }}>
              <strong>Внимание:</strong> в корзинах другого поставщика осталось{' '}
              {pending.itemCount} поз. у {pending.employeeCount}{' '}
              {pending.employeeCount === 1 ? 'сотрудника' : 'сотрудников'}. При переключении эти
              корзины будут <strong>аннулированы</strong> — сотрудникам придёт уведомление.
            </p>
          ) : (
            <p style={{ marginBottom: 0 }}>Незавершённых корзин у других поставщиков нет.</p>
          )}
        </div>
      ),
      okText: pending.willAnnul ? 'Аннулировать и переключить' : 'Переключить',
      cancelText: 'Отмена',
      okButtonProps: pending.willAnnul ? { danger: true } : undefined,
      onOk: async () => {
        setSwitchingMenu(true);
        try {
          const res = await linkRequestsAPI.setOrderingMenu(supplierId);
          setOrderingMenu((prev) =>
            prev ? { ...prev, orderingSupplierId: res.orderingSupplierId } : prev
          );
          if (res.annulled?.itemCount > 0) {
            message.warning(
              `Меню переключено на ${res.supplierName}. Аннулировано ${res.annulled.itemCount} поз. у ${res.annulled.employeeCount} сотрудников — им отправлено уведомление.`
            );
          } else {
            message.success(`Для заказов выбрано меню: ${res.supplierName}`);
          }
          await loadMenuForDate(selectedDate);
          await loadMyOrders();
        } catch (e: any) {
          message.error(e?.response?.data?.error || 'Не удалось переключить меню');
        } finally {
          setSwitchingMenu(false);
        }
      },
    });
  };

  useEffect(() => {
    if (!canOrderFood) return;
    linkRequestsAPI
      .getCartNotices()
      .then(({ notices }) => {
        if (!notices?.length) return;
        const ids = notices.map((n) => n.id);
        Modal.warning({
          title: 'Корзина аннулирована',
          content: notices[0].message,
          okText: 'Понятно',
          onOk: () => linkRequestsAPI.acknowledgeCartNotices(ids).catch(() => {}),
        });
      })
      .catch(() => {});
  }, [canOrderFood]);

  // Внеплановый режим — только сегодня: если открыли меню с датой «завтра», переключим
  useEffect(() => {
    if (emergencyStatus.active && !selectedDate.isSame(dayjs(), 'day')) {
      setSelectedDate(dayjs().startOf('day'));
    }
  }, [emergencyStatus.active]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMenuForDate = async (date: Dayjs) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const dateStr = date.format('YYYY-MM-DD');
      
      const response = await fetch(`${API_BASE_URL}/api/daily-menu/daily/${dateStr}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDayMenu(data);
        if (data.employeeCancelDeadlineTime !== undefined) {
          setEmployeeCancelDeadlineTime(data.employeeCancelDeadlineTime ?? null);
        }
        setEmergencyStatus({
          allowed: !!data.emergencyAllowed,
          active: !!data.emergencyActive,
          expiresAt: data.emergencyExpiresAt || null
        });
      } else {
        const error = await response.json().catch(() => ({}));
        if (error?.error) {
          message.warning(error.error);
        }
        setDayMenu(null);
        setEmergencyStatus({
          allowed: false,
          active: false,
          expiresAt: null
        });
      }
    } catch (error) {
      console.error('Error loading menu:', error);
      setDayMenu(null);
    } finally {
      setLoading(false);
    }
  };

  async function loadMyOrders() {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/api/daily-menu/my-weekly`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const list: OrderItem[] = Array.isArray(data) ? data : (data.items ?? []);
        setMyOrders(list);
        const combos: ComboOrderItem[] = Array.isArray(data) ? [] : (data.combos ?? []);
        setMyComboOrders(combos);
        if (!Array.isArray(data) && data.employeeCancelDeadlineTime !== undefined) {
          setEmployeeCancelDeadlineTime(data.employeeCancelDeadlineTime ?? null);
        }
        if (!Array.isArray(data) && data.cartStrippedReason) {
          message.warning(
            `Корзина очищена: ${data.cartStrippedReason} Можно заново выбрать блюда в пределах лимита.`
          );
        }

        // Загружаем статус подтверждений для всех дат с заказами
        if (!isCustomer && list.length > 0) {
          const dates = Array.from(new Set(list.map((o: OrderItem) => o.date)));
          const statusMap: Record<string, { submitted: boolean; allowEdit: boolean }> = {};
          
          await Promise.all(
            dates.map(async (date: string) => {
              try {
                const poolResp = await fetch(`${API_BASE_URL}/api/daily-menu/pool/${date}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (poolResp.ok) {
                  const poolData = await poolResp.json();
                  const userId = user?.id;
                  if (userId && poolData.submissionDetails?.[userId]) {
                    statusMap[date] = {
                      submitted: true,
                      allowEdit: poolData.submissionDetails[userId].allowEdit
                    };
                  } else if (userId && poolData.submittedUserIds?.includes(userId)) {
                    statusMap[date] = {
                      submitted: true,
                      allowEdit: false
                    };
                  } else {
                    statusMap[date] = {
                      submitted: false,
                      allowEdit: false
                    };
                  }
                }
              } catch (e) {
                console.error('Error loading submission status for', date, e);
              }
            })
          );
          
          setSubmissionStatus(statusMap);
        }
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  }

  const loadLimits = async (date?: Dayjs) => {
    if (!canOrderFood) return;
    try {
      const token = localStorage.getItem('token');
      const d = (date || selectedDate).format('YYYY-MM-DD');
      const response = await fetch(
        `${API_BASE_URL}/api/daily-menu/my-limits?date=${encodeURIComponent(d)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setLimitsInfo(data);
        if (data.cartStrippedReason) {
          message.warning(
            `Корзина очищена: ${data.cartStrippedReason} Можно заново выбрать блюда в пределах лимита.`
          );
          await loadMyOrders();
        }
      }
    } catch (error) {
      console.error('Error loading limits:', error);
    }
  };

  function canOrderForDate(date: Dayjs): boolean {
    const now = dayjs();
    const today = dayjs().startOf('day');
    
    if (emergencyStatus.active && !date.isSame(today, 'day')) {
      return false;
    }
    
    // Нельзя заказать на прошедшие дни
    if (date.isBefore(today, 'day')) {
      return false;
    }

    // Понедельник — только после вс 18:01 МСК (когда обновились лимиты)
    if (date.day() === 1) {
      const opensAt = date.subtract(1, 'day').hour(18).minute(1).second(0).millisecond(0);
      if (now.isBefore(opensAt)) {
        return false;
      }
    }
    
    // Проверяем дедлайн для сегодняшнего дня
    if (date.isSame(today, 'day') && dayMenu?.orderDeadline && !emergencyStatus.active) {
      const [hours, minutes] = dayMenu.orderDeadline.split(':').map(Number);
      const deadline = today.hour(hours).minute(minutes);
      
      if (now.isAfter(deadline)) {
        return false;
      }
    }
    
    // Можно заказать на неделю вперед
    const maxDate = today.add(7, 'days');
    return date.isSameOrBefore(maxDate, 'day');
  }

  const disabledDatePickerDate = React.useCallback((d: Dayjs | null) => {
    if (!d) return false;
    const today = dayjs().startOf('day');
    const maxDate = today.add(7, 'days');
    return d.isBefore(today, 'day') || d.isAfter(maxDate, 'day');
  }, []);

  const handleOrderItem = async (item: MenuItem) => {
    const dateStr = selectedDate.format('YYYY-MM-DD');

    const limitBlock = getLimitBlockForPrice(item.price);
    if (limitBlock) {
      message.error(limitBlock);
      return;
    }
    
    // Проверяем можно ли заказать
    if (!canOrderForDate(selectedDate)) {
      if (emergencyStatus.active && !selectedDate.isSame(dayjs(), 'day')) {
        message.warning('Внеплановый заказ доступен только на текущий день');
      } else if (selectedDate.day() === 1) {
        message.warning(
          'Заказ на понедельник откроется в воскресенье с 18:01 — после обновления лимитов по часам до 18:00'
        );
      } else if (selectedDate.isSame(dayjs(), 'day')) {
        message.warning(`Заказ на сегодня возможен только до ${dayMenu?.orderDeadline || '09:00'}`);
      } else {
        message.warning('Заказ на эту дату невозможен');
      }
      return;
    }

    if (!(await confirmSameDayOrder())) return;

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/api/daily-menu/add-item`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          menuItemId: item.id,
          date: dateStr,
        }),
      });

      if (response.ok) {
        message.success(`${item.name} добавлен в заказ на ${selectedDate.format('DD.MM.YYYY')}`);
        await loadMyOrders();
        loadLimits(selectedDate);
      } else {
        const error = await response.json();
        const errorMessage = error.error || error.message || 'Ошибка при добавлении заказа';
        
        // Показываем предупреждение для ошибок дедлайна, ошибку для остальных
        if (errorMessage.includes('время заказа') || errorMessage.includes('дедлайн') || errorMessage.includes('истекло')) {
          message.warning(errorMessage);
        } else {
          message.error(errorMessage);
        }
      }
    } catch (error) {
      message.error('Ошибка соединения с сервером');
    }
  };

  const hasOrderForDate = (date: Dayjs): boolean => {
    const dateStr = date.format('YYYY-MM-DD');
    return myOrders.some(order => order.date === dateStr);
  };

  const getOrdersForDate = (date: Dayjs): OrderItem[] => {
    const dateStr = date.format('YYYY-MM-DD');
    return myOrders.filter(order => order.date === dateStr);
  };

  const getOrderQuantity = (date: Dayjs, itemId: number): number => {
    const orders = getOrdersForDate(date);
    const order = orders.find(o => o.menuItemId === itemId);
    return order?.quantity || 0;
  };

  const dateCellRender = (date: Dayjs) => {
    const hasOrder = hasOrderForDate(date);
    const canOrder = canOrderForDate(date);
    const isPast = date.isBefore(dayjs(), 'day');
    const isToday = date.isSame(dayjs(), 'day');
    const orders = getOrdersForDate(date);
    const totalItems = orders.reduce((sum, o) => sum + o.quantity, 0);
    
    return (
      <div style={{ 
        minHeight: 50, 
        display: 'flex', 
        flexDirection: 'column',
        gap: 4,
        padding: '4px 0'
      }}>
        {isPast && (
          <Tag color="default" style={{ fontSize: 11, margin: 0 }}>
            Прошло
          </Tag>
        )}
        {isToday && canOrder && (
          <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>
            Сегодня
          </Tag>
        )}
        {hasOrder && (
          <Tag color="success" style={{ fontSize: 11, margin: 0 }}>
            ✓ {totalItems} шт
          </Tag>
        )}
        {!hasOrder && canOrder && !isToday && (
          <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
            Доступно
          </Tag>
        )}
      </div>
    );
  };

  const totalOrderCount = React.useMemo(
    () => myOrders.reduce((sum, order) => sum + order.quantity, 0),
    [myOrders]
  );

  const totalComboCount = React.useMemo(
    () => myComboOrders.reduce((sum, order) => sum + (order.quantity || 0), 0),
    [myComboOrders]
  );

  const ordersForSelectedDate = React.useMemo(
    () => getOrdersForDate(selectedDate),
    [selectedDate, myOrders]
  );

  const ordersGroupedByDate = React.useMemo(() => {
    const groups: Record<string, OrderItem[]> = {};
    myOrders.forEach((o) => {
      if (!groups[o.date]) groups[o.date] = [];
      groups[o.date].push(o);
    });
    // сортировка по дате возр.
    Object.keys(groups).forEach((k) => {
      groups[k].sort((a, b) => a.menuItem.name.localeCompare(b.menuItem.name));
    });
    return groups;
  }, [myOrders]);

  const totalAmountForSelectedDate = React.useMemo(
    () =>
      ordersForSelectedDate.reduce(
        (sum, order) => sum + (order.menuItem?.price || 0) * order.quantity,
        0
      ),
    [ordersForSelectedDate]
  );

  const modalFooter: React.ReactNode[] = [
    <Button key="close" onClick={() => setModalVisible(false)}>
      Закрыть
    </Button>
  ];

  if (isCustomerOrManager) {
    modalFooter.push(
      <Button
        key="confirm"
        type="primary"
        icon={<CheckCircleOutlined />}
        loading={confirming}
        disabled={ordersForSelectedDate.length === 0}
        onClick={handleConfirmOrder}
      >
        Отправить заказ
      </Button>
    );
  }

  const showMyOrdersModal = () => {
    setModalVisible(true);
    loadLimits();
  };

  const getOrderForItem = (date: Dayjs, itemId: number) => {
    const orders = getOrdersForDate(date);
    return orders.find((o) => o.menuItemId === itemId);
  };

  const handleDecreaseItem = async (item: MenuItem) => {
    const existing = getOrderForItem(selectedDate, item.id);
    if (!existing) return;

    try {
      const token = localStorage.getItem('token');
      const query = existing.quantity > 1 ? '?decrement=true' : '';
      const response = await fetch(
        `${API_BASE_URL}/api/daily-menu/remove-item/${existing.id}${query}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        await loadMyOrders();
        if (modalVisible) {
          loadLimits();
        }
      } else {
        const error = await response.json();
        message.error(error.error || 'Не удалось обновить заказ');
      }
    } catch {
      message.error('Ошибка соединения с сервером');
    }
  };

  const handleIncreaseOrderItem = async (order: OrderItem) => {
    // Увеличиваем количество, добавляя то же блюдо еще раз
    const orderDate = dayjs(order.date);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/daily-menu/add-item`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          menuItemId: order.menuItemId,
          date: order.date,
        }),
      });

      if (response.ok) {
        await loadMyOrders();
        if (modalVisible) {
          loadLimits();
        }
      } else {
        const error = await response.json();
        const errorMessage = error.error || error.message || 'Ошибка при добавлении заказа';
        
        // Показываем предупреждение для ошибок дедлайна, ошибку для остальных
        if (errorMessage.includes('время заказа') || errorMessage.includes('дедлайн') || errorMessage.includes('истекло')) {
          message.warning(errorMessage);
        } else {
          message.error(errorMessage);
        }
      }
    } catch (error) {
      message.error('Ошибка соединения с сервером');
    }
  };

  const handleDecreaseOrderItem = async (order: OrderItem) => {
    if (order.quantity <= 1) {
      // Если количество 1, удаляем позицию полностью
      handleRemoveOrderItem(order.id);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/daily-menu/remove-item/${order.id}?decrement=true`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        await loadMyOrders();
        if (modalVisible) {
          loadLimits();
        }
      } else {
        const error = await response.json();
        message.error(error.error || 'Не удалось уменьшить количество');
      }
    } catch {
      message.error('Ошибка соединения с сервером');
    }
  };

  const handleRemoveOrderItem = async (orderItemId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/daily-menu/remove-item/${orderItemId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        await loadMyOrders();
        if (modalVisible) {
          loadLimits();
        }
      } else {
        const error = await response.json();
        message.error(error.error || 'Не удалось удалить позицию');
      }
    } catch {
      message.error('Ошибка соединения с сервером');
    }
  };

  const savePoolItemNote = async (poolItemId: number, note: string | null) => {
    const trimmed = note?.trim() || null;
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_BASE_URL}/api/daily-menu/pool-item/${poolItemId}/note`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note: trimmed })
      });
      if (resp.ok) {
        const data = await resp.json().catch(() => ({}));
        const saved =
          data.employeeNote !== undefined && data.employeeNote !== null
            ? data.employeeNote
            : trimmed;
        setMyOrders((prev) =>
          prev.map((o) => (o.id === poolItemId ? { ...o, employeeNote: saved ?? null } : o))
        );
      } else {
        const err = await resp.json().catch(() => ({}));
        message.error(err?.error || 'Не удалось сохранить комментарий');
      }
    } catch {
      message.error('Ошибка соединения');
    }
  };

  const handleEmployeeWithdraw = (dateKey: string) => {
    Modal.confirm({
      title: 'Отказаться от заказа?',
      content:
        'Все ваши позиции на выбранный день будут сняты. Если заказ уже у поставщика, сумма заказа пересчитается.',
      okText: 'Да, отказаться',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      async onOk() {
        try {
          const token = localStorage.getItem('token');
          const resp = await fetch(`${API_BASE_URL}/api/daily-menu/employee-withdraw`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ date: dateKey })
          });
          if (resp.ok) {
            message.success('Отказ зарегистрирован');
            await loadMyOrders();
            loadMenuForDate(selectedDate);
            if (modalVisible) {
              loadLimits();
            }
          } else {
            const err = await resp.json().catch(() => ({}));
            message.error(err?.error || 'Не удалось выполнить отказ');
            throw new Error('withdraw failed');
          }
        } catch (e) {
          if ((e as Error)?.message !== 'withdraw failed') {
            message.error('Ошибка соединения');
          }
          throw e;
        }
      }
    });
  };

  async function handleConfirmOrder() {
    if (!isCustomerOrManager) {
      message.info('Отправить заказ может только ответственный заказчик');
      return;
    }
    if (ordersForSelectedDate.length === 0) {
      message.warning('Нет позиций для отправки');
      return;
    }

    setConfirming(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/daily-menu/confirm`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: selectedDate.format('YYYY-MM-DD')
        })
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const excluded: string[] = data?.excludedEmployees ?? [];
        if (excluded.length > 0) {
          message.warning(
            `Заказ отправлен. Исключены из отправки (лимит): ${excluded.join('; ')}`
          );
        } else {
          message.success('Заказ отправлен поставщику');
        }
        setModalVisible(false);
        loadMyOrders();
        loadMenuForDate(selectedDate);
      } else {
        const error = await response.json();
        message.error(error.error || 'Не удалось подтвердить заказ');
      }
    } catch {
      message.error('Ошибка соединения с сервером');
    } finally {
      setConfirming(false);
    }
  }

  return (
    <div>
      {isCustomerOrManager && orderingMenu && orderingMenu.suppliers.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Text strong>Меню какого поставщика доступно сотрудникам</Text>
            {orderingMenu.canSwitch ? (
              <>
                <Radio.Group
                  value={orderingMenu.orderingSupplierId ?? undefined}
                  onChange={(e) => handleSwitchOrderingMenu(e.target.value)}
                  disabled={switchingMenu}
                  optionType="button"
                  buttonStyle="solid"
                  style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
                >
                  {orderingMenu.suppliers.map((s) => (
                    <Radio.Button key={s.id} value={s.id}>
                      {s.name}
                    </Radio.Button>
                  ))}
                </Radio.Group>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Сотрудники не выбирают поставщика сами — видят только выбранное здесь меню. При
                  переключении незавершённые корзины другого поставщика аннулируются.
                </Text>
              </>
            ) : (
              <Text>
                Сейчас для заказов:{' '}
                <Tag color="blue">
                  {orderingMenu.suppliers.find((s) => s.id === orderingMenu.orderingSupplierId)?.name ||
                    orderingMenu.suppliers[0]?.name}
                </Tag>
              </Text>
            )}
          </Space>
        </Card>
      )}
      <Card
        title="Меню на неделю"
        extra={
          <Space>
            <Button
              type="primary"
              icon={<ShoppingCartOutlined />}
              onClick={showMyOrdersModal}
            >
              Мои заказы {(totalOrderCount + totalComboCount) > 0 && `(${totalOrderCount + totalComboCount})`}
            </Button>
          </Space>
        }
      >
        {!isMobile && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Один день = меню и заказ"
            description="Вы выбираете день в календаре — открывается меню на этот день, и все блюда из него попадают в заказ именно на этот день. Отдельной «даты заказа» нет. По умолчанию открыт завтрашний день."
          />
        )}
        {isMobile ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
              <div style={{ minWidth: 220, flex: 1 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
                  День меню (на какую дату заказ)
                </Text>
                <DatePicker
                  value={selectedDate}
                  onChange={(v) => v && setSelectedDate(v)}
                  allowClear={false}
                  disabledDate={disabledDatePickerDate as any}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ minWidth: 160, display: 'flex', justifyContent: 'flex-end' }}>
                <Badge count={totalOrderCount + totalComboCount} size="small">
                  <Button icon={<ShoppingCartOutlined />} onClick={showMyOrdersModal}>
                    Корзина
                  </Button>
                </Badge>
              </div>
            </Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Блюда берутся из меню выбранного дня — заказ тоже идёт на этот день. По умолчанию
              открыто завтра; день можно сменить.
            </Text>
          </Space>
        ) : (
          <Calendar
            value={selectedDate}
            onSelect={setSelectedDate}
            fullscreen={false}
            fullCellRender={(date) => {
              const isPast = date.isBefore(dayjs(), 'day');
              const isSelected = date.isSame(selectedDate, 'day');
              const isToday = date.isSame(dayjs(), 'day');
              const hasOrder = hasOrderForDate(date);
              const canOrder = canOrderForDate(date);
              const orders = getOrdersForDate(date);
              const totalItems = orders.reduce((sum, o) => sum + o.quantity, 0);

              return (
                <div
                  style={{
                    height: '100%',
                    background: isPast
                      ? '#f5f5f5'
                      : isToday
                      ? '#fff7e6'
                      : isSelected
                      ? '#e6f7ff'
                      : 'white',
                    border: isSelected ? '2px solid #1890ff' : '1px solid #f0f0f0',
                    borderRadius: 4,
                    padding: 8,
                    cursor: isPast ? 'not-allowed' : 'pointer',
                    opacity: isPast ? 0.6 : 1,
                    transition: 'all 0.3s'
                  }}
                >
                  <div style={{ fontSize: 16, fontWeight: isToday ? 'bold' : 'normal' }}>
                    {date.date()}
                  </div>
                  <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {isPast && (
                      <Tag color="default" style={{ fontSize: 10, margin: 0 }}>
                        Прошло
                      </Tag>
                    )}
                    {isToday && (
                      <Tag color="orange" style={{ fontSize: 10, margin: 0 }}>
                        Сегодня
                      </Tag>
                    )}
                    {hasOrder && (
                      <Tag color="success" style={{ fontSize: 10, margin: 0 }}>
                        ✓ {totalItems} шт
                      </Tag>
                    )}
                    {!hasOrder && canOrder && !isToday && !isPast && (
                      <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                        Доступно
                      </Tag>
                    )}
                  </div>
                </div>
              );
            }}
          />
        )}
      </Card>

      <Card
        title={
          <Space>
            <span>Меню на {selectedDate.format('DD MMMM YYYY')}</span>
            {dayMenu?.supplier?.name && (
              <Tag color="blue">{dayMenu.supplier.name}</Tag>
            )}
            {selectedDate.isBefore(dayjs(), 'day') && (
              <Tag color="error">Прошедшая дата</Tag>
            )}
            {selectedDate.isSame(dayjs(), 'day') && (
              <Tag color="warning">Сегодня</Tag>
            )}
          </Space>
        }
        style={{ marginTop: 16 }}
        loading={loading}
        extra={
          <Space>
            {dayMenu?.orderDeadline && selectedDate.isSame(dayjs(), 'day') && (
              <Space>
                <ClockCircleOutlined />
                <Text type="secondary">Заказ до {dayMenu.orderDeadline}</Text>
              </Space>
            )}
            {!isCustomer &&
              employeeCancelDeadlineTime &&
              canOrderFood && (
                <Text type="secondary">
                  Отказ сотрудника до {employeeCancelDeadlineTime} в день доставки
                </Text>
              )}
          </Space>
        }
      >
        {!isCustomer && canOrderFood && employeeCancelDeadlineTime && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Отказ от заказа"
            description={`Пока не наступило ${employeeCancelDeadlineTime} в день доставки, вы можете изменить корзину после подтверждения или полностью отказаться (в том числе если заказ уже отправлен заказчиком).`}
          />
        )}
        {isCustomer && dayMenu?.minOrders && (
          <div style={{ 
            background: '#e6f7ff', 
            border: '1px solid #91d5ff',
            borderRadius: 4,
            padding: 12,
            marginBottom: 16 
          }}>
            <Space>
              <InfoCircleOutlined style={{ color: '#1890ff' }} />
              <Text>
                Минимальное количество заказов: {dayMenu.minOrders}
                {dayMenu.currentOrders !== undefined && (
                  <> (сейчас: {dayMenu.currentOrders})</>
                )}
              </Text>
            </Space>
          </div>
        )}

        {isCustomer && dayMenu?.minOrderAmount && dayMenu.minOrderAmount > 0 && (
          <div style={{ 
            background: '#fffbe6', 
            border: '1px solid #ffe58f',
            borderRadius: 4,
            padding: 12,
            marginBottom: 16 
          }}>
            <Space>
              <InfoCircleOutlined style={{ color: '#d48806' }} />
              <Text>
                Минимальная сумма заказа: {dayMenu.minOrderAmount} ₽
              </Text>
            </Space>
          </div>
        )}

        {dayMenu?.discountApplied && dayMenu.discountPercent && (
          <div style={{ 
            background: '#f6ffed', 
            border: '1px solid #b7eb8f',
            borderRadius: 4,
            padding: 12,
            marginBottom: 16 
          }}>
            <Space>
              <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>🎉</Text>
              <Text strong style={{ color: '#52c41a' }}>
                Вам предоставлена скидка {dayMenu.discountPercent}% на все блюда!
              </Text>
            </Space>
          </div>
        )}

        {emergencyStatus.active && (
          <Alert
            type="warning"
            showIcon
            message="Внеплановый заказ активен"
            description={
              emergencyStatus.expiresAt
                ? `Можно заказать только на сегодня. Режим отключится ${dayjs(emergencyStatus.expiresAt).format('DD.MM HH:mm')}`
                : 'Можно заказать только на сегодня.'
            }
            style={{ marginBottom: 16 }}
          />
        )}

        {isOrderingForToday && !emergencyStatus.active && (
          <Alert
            type="warning"
            showIcon
            message="Открыто меню на сегодня"
            description={
              <span>
                Блюда из этого меню попадут в заказ на сегодня ({selectedDate.format('DD.MM.YYYY')}).
                Обычно нужен завтрашний день ({dayjs().add(1, 'day').format('DD.MM.YYYY')})
                {dayMenu?.orderDeadline ? ` — на сегодня дедлайн ${dayMenu.orderDeadline}` : ''}.{' '}
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto' }}
                  onClick={() => setSelectedDate(dayjs().add(1, 'day').startOf('day'))}
                >
                  Открыть меню на завтра
                </Button>
              </span>
            }
            style={{ marginBottom: 16 }}
          />
        )}

        {selectedDate.isBefore(dayjs(), 'day') && (
          <div style={{ 
            background: '#fff1f0', 
            border: '1px solid #ffccc7',
            borderRadius: 4,
            padding: 16,
            marginBottom: 16,
            textAlign: 'center'
          }}>
            <Text type="danger" strong style={{ fontSize: 16 }}>
              ⚠️ Это меню из прошлого. Заказ на эту дату невозможен.
            </Text>
          </div>
        )}

        {(dayMenu?.combos?.length || 0) > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Title level={5} style={{ marginTop: 0 }}>
              Комбо-обеды
            </Title>
            {dayMenu?.combosOnly && (
              <Alert
                type="info"
                showIcon
                message="На эту дату доступны только комбо-обеды"
                style={{ marginBottom: 12 }}
              />
            )}
            <List
              grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 4, xxl: 4 }}
              dataSource={dayMenu?.combos || []}
              renderItem={(combo) => (
                <List.Item>
                  <Card
                    hoverable
                    title={combo.title}
                    extra={
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                          if (combo.allowCustomization) {
                            openComboOrderModal(combo);
                          } else {
                            submitComboOrder(combo);
                          }
                        }}
                        disabled={
                          !canOrderForDate(selectedDate) ||
                          !!getLimitBlockForPrice(computeComboDisplayPrice(combo))
                        }
                        title={
                          getLimitBlockForPrice(computeComboDisplayPrice(combo)) || undefined
                        }
                      >
                        Заказать
                      </Button>
                    }
                  >
                    {combo.description && (
                      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                        {combo.description}
                      </Text>
                    )}
                    <Space wrap>
                      <Tag color="purple">{combo.pricingMode === 'fixed' ? 'Фикс. цена' : 'Сумма блюд'}</Tag>
                      {combo.allowCustomization && <Tag color="blue">Можно выбрать варианты</Tag>}
                      <Tag color="green">{computeComboDisplayPrice(combo)} ₽</Tag>
                    </Space>
                    <div style={{ marginTop: 10 }}>
                      {(combo.groups || []).map((g) => (
                        <div key={g.id} style={{ marginBottom: 6 }}>
                          <Text strong>{g.title}</Text>
                          <div style={{ marginTop: 4 }}>
                            <Space wrap>
                              {(g.items || []).map((it) => (
                                <Tag key={it.id}>{it.menuItem?.name || `#${it.menuItemId}`}</Tag>
                              ))}
                            </Space>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </List.Item>
              )}
            />
          </div>
        )}

        {dayMenu?.combosOnly ? (
          <Empty description="На эту дату доступны только комбо-обеды" />
        ) : !dayMenu || !dayMenu.items || dayMenu.items.length === 0 ? (
          <Empty description="Меню на эту дату пока не добавлено" />
        ) : (
          <List
            grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 4, xxl: 4 }}
            dataSource={dayMenu.items}
            renderItem={(item) => {
              const quantity = getOrderQuantity(selectedDate, item.id);
              const isOrdered = quantity > 0;
              const coverSrc = resolveImageUrl(item.thumbnailUrl || item.imageUrl);
              const fullSrc = resolveImageUrl(item.imageUrl);
              
              return (
                <List.Item>
                  <Card
                    hoverable
                    cover={
                      coverSrc ? (
                        <Image
                          alt={item.name}
                          src={coverSrc}
                          height={isMobile ? 140 : 200}
                          style={{ objectFit: 'cover' }}
                          preview={fullSrc ? { src: fullSrc } : false}
                        />
                      ) : (
                        <div style={{ 
                          height: isMobile ? 140 : 200, 
                          background: '#f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Text type="secondary">Нет фото</Text>
                        </div>
                      )
                    }
                  >
                    <Card.Meta
                      title={item.name}
                      description={
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          {item.description && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {item.description}
                            </Text>
                          )}
                          <div>
                            <Space size={[4, 4]} wrap>
                              <Tag color="blue">{categoryLabel(item.category)}</Tag>
                              <Tag>{(item as any).weightText || `${item.weight} г`}</Tag>
                              <Tag>{typeof item.calories === 'number' ? `${item.calories} ккал` : '— ккал'}</Tag>
                              {item.isVegan && <Tag color="green">🌱 Веган</Tag>}
                              {item.isLenten && <Tag color="blue">⛪ Постное</Tag>}
                              {item.isHalal && <Tag color="orange">☪️ Халяль</Tag>}
                            </Space>
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: 8
                          }}>
                            <div>
                              <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
                                {item.price} ₽
                              </Text>
                              {item.discountPercent && item.originalPrice && (
                                <div style={{ marginTop: 2 }}>
                                  <Text 
                                    type="secondary" 
                                    style={{ 
                                      textDecoration: 'line-through', 
                                      fontSize: 12 
                                    }}
                                  >
                                    {item.originalPrice} ₽
                                  </Text>
                                  <Tag 
                                    color="green" 
                                    style={{ marginLeft: 4, fontSize: '12px' }}
                                  >
                                    -{item.discountPercent}%
                                  </Tag>
                                </div>
                              )}
                            </div>
                            {(() => {
                              const dateStr = selectedDate.format('YYYY-MM-DD');
                              const submission = submissionStatus[dateStr];
                              const isSubmitted = submission?.submitted || false;
                              const allowEdit = submission?.allowEdit || false;
                              const dayOrders = getOrdersForDate(selectedDate);
                              const poolLocked = isPoolLocked(dayOrders);
                              const canEdit =
                                (!isSubmitted || allowEdit || isEmployeeCancelWindowOpen(dateStr)) &&
                                !poolLocked;
                              const limitBlock = getLimitBlockForPrice(item.price);
                              const canOrder =
                                canOrderForDate(selectedDate) && canEdit && !limitBlock;
                              
                              return quantity > 0 ? (
                                <Space>
                                  <Button
                                    size="small"
                                    icon={<MinusOutlined />}
                                    onClick={() => handleDecreaseItem(item)}
                                    disabled={!canEdit}
                                    title={!canEdit ? 'Редактирование запрещено. Обратитесь к заказчику.' : 'Уменьшить количество'}
                                  />
                                  <Tag color="success" style={{ margin: 0 }}>
                                    {quantity} шт
                                  </Tag>
                                  <Button
                                    type="primary"
                                    size="small"
                                    icon={<PlusOutlined />}
                                    onClick={() => handleOrderItem(item)}
                                    disabled={!canOrder}
                                    title={
                                      limitBlock ||
                                      (!canOrder
                                        ? !canEdit
                                          ? 'Редактирование запрещено. Обратитесь к заказчику.'
                                          : 'Заказ на эту дату невозможен'
                                        : 'Увеличить количество')
                                    }
                                  >
                                    Ещё
                                  </Button>
                                </Space>
                              ) : (
                                <Button
                                  type="primary"
                                  size="small"
                                  icon={<ShoppingCartOutlined />}
                                  onClick={() => handleOrderItem(item)}
                                  disabled={!canOrder}
                                  title={
                                    limitBlock ||
                                    (!canOrder
                                      ? !canEdit
                                        ? 'Редактирование запрещено. Обратитесь к заказчику.'
                                        : 'Заказ на эту дату невозможен'
                                      : 'Добавить в заказ')
                                  }
                                  block={isMobile}
                                >
                                  Заказать
                                </Button>
                              );
                            })()}
                          </div>
                        </Space>
                      }
                    />
                  </Card>
                </List.Item>
              );
            }}
          />
        )}
      </Card>

      <Modal
        title="Мои заказы"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={modalFooter}
        width={isMobile ? '100%' : 700}
        centered={!isMobile}
        style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
      >
        {!isCustomerOrManager && (
          <Alert
            type="info"
            showIcon
            message="Кнопка «Подтвердить» отмечает ваш заказ для заказчика. Отправить пул поставщику может только заказчик (или контролёр)."
            style={{ marginBottom: 16 }}
          />
        )}
        {limitsInfo && limitsInfo.hasLimits && limitsInfo.canOrder === false && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="Заказ заблокирован лимитом"
            description={
              limitsInfo.blockReason ||
              'Удалите лишние позиции из корзины или обратитесь к заказчику для изменения лимита.'
            }
          />
        )}
        {limitsInfo && limitsInfo.hasLimits && (
          <Alert
            type={limitsInfo.canOrder === false ? 'warning' : 'info'}
            showIcon
            style={{ marginBottom: 16 }}
            message="Лимиты заказов"
            description={
              <Space direction="vertical" size="small">
                {limitsInfo.dailyLimit !== null && (
                  <div>
                    <Text strong>Дневной лимит:</Text> Потрачено {limitsInfo.dailySpent} ₽ из {limitsInfo.dailyLimit} ₽
                    {limitsInfo.dailyRemaining !== null && (
                      <Tag color={limitsInfo.dailyRemaining > 0 ? 'green' : 'red'} style={{ marginLeft: 8 }}>
                        Осталось: {limitsInfo.dailyRemaining} ₽
                      </Tag>
                    )}
                  </div>
                )}
                {limitsInfo.monthlyLimit !== null && (
                  <div>
                    <Text strong>Месячный лимит:</Text> Потрачено {limitsInfo.monthlySpent} ₽ из {limitsInfo.monthlyLimit} ₽
                    {limitsInfo.monthlyRemaining !== null && (
                      <Tag color={limitsInfo.monthlyRemaining > 0 ? 'green' : 'red'} style={{ marginLeft: 8 }}>
                        Осталось: {limitsInfo.monthlyRemaining} ₽
                      </Tag>
                    )}
                  </div>
                )}
              </Space>
            }
          />
        )}
        {myComboOrders.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Комбо в корзине
            </Text>
            <List
              dataSource={myComboOrders}
              renderItem={(c) => (
                <List.Item
                  actions={[
                    <Button
                      key="plus"
                      size="small"
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        const selections: Record<number, number[]> = {};
                        (c.selectionsSnapshot || []).forEach((g) => {
                          selections[g.groupId] = (g.items || []).map((it) => it.menuItemId);
                        });
                        submitComboOrder(c.combo, selections);
                      }}
                    />,
                    <Button
                      key="minus"
                      size="small"
                      icon={<MinusOutlined />}
                      onClick={() => removeComboOrderItem(c.id, true)}
                      disabled={c.quantity <= 1}
                    />,
                    <Button
                      key="remove"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeComboOrderItem(c.id)}
                    >
                      Удалить
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <span>{c.combo?.title || `Комбо #${c.comboId}`}</span>
                        <Tag color="blue">x{c.quantity}</Tag>
                        <Tag>{dayjs(c.date).format('DD.MM.YYYY')}</Tag>
                      </Space>
                    }
                    description={<Text type="secondary">{Math.round((c.unitPrice || 0) * c.quantity * 100) / 100} ₽</Text>}
                  />
                </List.Item>
              )}
            />
          </div>
        )}

        {myOrders.length === 0 && myComboOrders.length === 0 ? (
          <Empty description="Нет позиций в корзине" />
        ) : (
          Object.keys(ordersGroupedByDate)
            .sort()
            .map((dateKey) => {
              const orders = ordersGroupedByDate[dateKey];
              const dayTotal = orders.reduce(
                (sum, o) => sum + (o.menuItem?.price || 0) * o.quantity,
                0
              );
              const submission = submissionStatus[dateKey];
              const isSubmitted = submission?.submitted || false;
              const allowEdit = submission?.allowEdit || false;
              const poolSent = orders.some((o) => o.poolStatus === 'sent');
              const poolLocked = isPoolLocked(orders);
              const canEditLines =
                (!isSubmitted || allowEdit || isEmployeeCancelWindowOpen(dateKey)) && !poolLocked;
              const showWithdraw =
                canOrderFood &&
                employeeCancelDeadlineTime &&
                isEmployeeCancelWindowOpen(dateKey) &&
                orders.length > 0;

              return (
                <Card
                  key={dateKey}
                  size="small"
                  title={dayjs(dateKey).format('DD.MM.YYYY')}
                  style={{ marginBottom: 16 }}
                  extra={
                    <Space>
                      <Tag color="green">Итого: {dayTotal} ₽</Tag>
                      {poolLocked && <Tag color="blue">У поставщика</Tag>}
                      {poolSent && !poolLocked && <Tag color="orange">Черновик у заказчика</Tag>}
                      {isSubmitted && (
                        <Tag color={allowEdit ? 'orange' : 'green'} icon={<CheckCircleOutlined />}>
                          {allowEdit ? 'Редактирование разрешено' : 'Подтверждено'}
                        </Tag>
                      )}
                      {showWithdraw && (
                        <Button
                          size="small"
                          danger
                          onClick={() => handleEmployeeWithdraw(dateKey)}
                        >
                          Отказаться от заказа
                        </Button>
                      )}
                      <Button
                        size="small"
                        type="primary"
                        disabled={isSubmitted}
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const resp = await fetch(`${API_BASE_URL}/api/daily-menu/employee-submit`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({ date: dateKey })
                            });
                            if (resp.ok) {
                              message.success(
                                poolSent
                                  ? 'Ваш заказ отмечен для заказчика'
                                  : 'Отправлено заказчику для подтверждения'
                              );
                              loadMyOrders();
                            } else {
                              const err = await resp.json().catch(() => ({}));
                              message.error(err?.error || 'Не удалось отправить');
                            }
                          } catch {
                            message.error('Ошибка соединения');
                          }
                        }}
                      >
                        {isSubmitted ? 'Подтверждено' : 'Подтвердить'}
                      </Button>
                    </Space>
                  }
                >
                  {isSubmitted && !allowEdit && !isEmployeeCancelWindowOpen(dateKey) && (
                    <Alert
                      type="warning"
                      showIcon
                      message="Заказ подтвержден"
                      description="Для добавления позиций обратитесь к ответственному заказчику для получения разрешения на редактирование."
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  {poolSent && !poolLocked && (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Пул передан в обработку"
                      description="Заказчик ещё не подтвердил отправку поставщику — вы можете менять состав корзины. Кнопка «Подтвердить» отмечает ваш заказ для заказчика; поставщику заказ уходит только после кнопки «Отправить заказ» у ответственного заказчика."
                    />
                  )}
                  {poolLocked && showWithdraw && (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Заказ у поставщика"
                      description="Изменить состав по позициям нельзя; при необходимости воспользуйтесь полным отказом, пока не истёк срок, заданный поставщиком."
                    />
                  )}
                  <List
                    dataSource={orders}
                    renderItem={(order) => {
                      const orderAvatarSrc = resolveImageUrl(
                        order.menuItem.thumbnailUrl || order.menuItem.imageUrl
                      );
                      const orderAvatarPreview = resolveImageUrl(order.menuItem.imageUrl);
                      return (
                        <List.Item
                          actions={[
                            <Button
                              key="plus"
                              size="small"
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={() => handleIncreaseOrderItem(order)}
                              disabled={!canEditLines}
                              title={
                                !canEditLines
                                  ? poolLocked
                                    ? 'Заказ уже у поставщика'
                                    : 'Редактирование запрещено. Обратитесь к заказчику.'
                                  : 'Увеличить количество'
                              }
                            />,
                            <Button
                              key="minus"
                              size="small"
                              icon={<MinusOutlined />}
                              onClick={() => handleDecreaseOrderItem(order)}
                              disabled={order.quantity <= 1 || !canEditLines}
                              title={
                                !canEditLines
                                  ? poolLocked
                                    ? 'Заказ уже у поставщика'
                                    : 'Редактирование запрещено. Обратитесь к заказчику.'
                                  : 'Уменьшить количество'
                              }
                            />,
                            <Button
                              key="remove"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => handleRemoveOrderItem(order.id)}
                              disabled={!canEditLines}
                              title={
                                !canEditLines
                                  ? poolLocked
                                    ? 'Заказ уже у поставщика'
                                    : 'Редактирование запрещено. Обратитесь к заказчику.'
                                  : 'Удалить позицию'
                              }
                            >
                              Удалить
                            </Button>
                          ]}
                        >
                          <List.Item.Meta
                            avatar={
                              orderAvatarSrc ? (
                                <Image
                                  src={orderAvatarSrc}
                                  width={60}
                                  height={60}
                                  style={{ objectFit: 'cover', borderRadius: 4 }}
                                  preview={
                                    orderAvatarPreview ? { src: orderAvatarPreview } : false
                                  }
                                />
                              ) : null
                            }
                            title={
                              <Space>
                                <span>{order.menuItem.name}</span>
                                <Tag color="blue">x{order.quantity}</Tag>
                              </Space>
                            }
                            description={
                              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Space>
                                  <Text strong>
                                    {(order.menuItem.price || 0) * order.quantity} ₽
                                  </Text>
                                  {order.quantity > 1 && (
                                    <Text type="secondary">
                                      {(order.menuItem.price || 0)} ₽ × {order.quantity}
                                    </Text>
                                  )}
                                </Space>
                                {canEditLines ? (
                                  <Input.TextArea
                                    rows={2}
                                    maxLength={500}
                                    showCount
                                    placeholder="Комментарий к блюду (например, без лука)"
                                    value={order.employeeNote ?? ''}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setMyOrders((prev) =>
                                        prev.map((o) =>
                                          o.id === order.id ? { ...o, employeeNote: v } : o
                                        )
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const raw = e.target.value;
                                      const v = raw.trim() || null;
                                      if (raw !== (v ?? '')) {
                                        setMyOrders((prev) =>
                                          prev.map((o) =>
                                            o.id === order.id ? { ...o, employeeNote: v } : o
                                          )
                                        );
                                      }
                                      savePoolItemNote(order.id, v);
                                    }}
                                  />
                                ) : order.employeeNote ? (
                                  <Text type="secondary">Комментарий: {order.employeeNote}</Text>
                                ) : null}
                              </Space>
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                </Card>
              );
            })
        )}
      </Modal>

      <Modal
        open={comboModalOpen}
        title={comboModalCombo ? `Комбо: ${comboModalCombo.title}` : 'Комбо'}
        onCancel={() => setComboModalOpen(false)}
        okText="Добавить в корзину"
        cancelText="Отмена"
        okButtonProps={{
          disabled: !comboModalCombo || comboSelectionErrors.length > 0
        }}
        onOk={() => {
          if (!comboModalCombo) return;
          submitComboOrder(comboModalCombo, comboSelections);
        }}
      >
        {!comboModalCombo ? null : (
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            {comboModalCombo.description ? (
              <Text type="secondary">{comboModalCombo.description}</Text>
            ) : null}
            <Alert
              type="info"
              showIcon
              message={
                comboModalCombo.pricingMode === 'fixed'
                  ? `Цена набора: ${computeComboDisplayPrice(comboModalCombo)} ₽`
                  : `Цена зависит от выбранных блюд`
              }
              description={
                comboModalCombo.pricingMode === 'fixed'
                  ? 'Итоговая сумма фиксирована — от выбора вариантов внутри набора не зависит.'
                  : undefined
              }
            />

            {(comboModalCombo.groups || []).map((g) => {
              const isFixed = comboModalCombo.pricingMode === 'fixed';
              const options =
                (g.items || [])
                  .map((it) => it.menuItem)
                  .filter(Boolean)
                  .map((mi) => ({
                    label: isFixed
                      ? mi.name
                      : `${mi.name} (${mi.price ?? 0} ₽)`,
                    value: mi.id
                  })) ?? [];

              const required = g.requiredCount || 1;
              const value = comboSelections[g.id] || [];

              return (
                <div key={g.id}>
                  <Space direction="vertical" size={6} style={{ width: '100%' }}>
                    <Text strong>
                      {g.title} (выберите {required})
                    </Text>
                    <Select
                      mode="multiple"
                      style={{ width: '100%' }}
                      placeholder="Выберите варианты"
                      options={options}
                      value={value}
                      onChange={(vals) => {
                        const normalized = (vals || []).slice(0, required);
                        setComboSelections((prev) => ({ ...prev, [g.id]: normalized }));
                      }}
                    />
                  </Space>
                </div>
              );
            })}

            {comboSelectionErrors.length > 0 ? (
              <Alert
                type="warning"
                showIcon
                message="Нужно выбрать варианты"
                description={
                  <div>
                    {comboSelectionErrors.map((e) => (
                      <div key={e}>{e}</div>
                    ))}
                  </div>
                }
              />
            ) : null}
          </Space>
        )}
      </Modal>

      {isMobile ? (
        <div
          style={{
            position: 'fixed',
            left: 12,
            right: 12,
            bottom: 12,
            zIndex: 1000,
          }}
        >
          <Badge count={totalOrderCount + totalComboCount} size="small" offset={[-10, 8]}>
            <Button
              type="primary"
              icon={<ShoppingCartOutlined />}
              onClick={showMyOrdersModal}
              block
              style={{
                height: 44,
                borderRadius: 12,
                boxShadow: '0 14px 40px rgba(16,24,40,0.22)',
              }}
            >
              Мои заказы
            </Button>
          </Badge>
        </div>
      ) : null}
    </div>
  );
};

export default Menu;
